import { ExclamationTriangleIcon } from '@radix-ui/react-icons'
import { Alert, AlertDescription, AlertTitle } from '@repo/ui/components/ui/alert'
import { Avatar, AvatarFallback, AvatarImage } from '@repo/ui/components/ui/avatar'
import { Button } from '@repo/ui/components/ui/button'
import { CardContent } from '@repo/ui/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/components/ui/dialog'
import { Input } from '@repo/ui/components/ui/input'
import { Label } from '@repo/ui/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components/ui/select'
import { Textarea } from '@repo/ui/components/ui/textarea'
import { surfaceRadii } from '@repo/ui/lib/design-tokens'
import { cn } from '@repo/ui/lib/utils'
import {
  type ComponentPropsWithoutRef,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { toast } from 'sonner'
import { AppWorkspaceContentPanel, WorkspaceSectionCard } from '@/components/app/workspace-shell'
import { PremiumPanel } from '@/components/ui/premium-panel'
import { createTrpcClient } from '@/lib/config/trpc-client'
import { FORM, LAYOUT, RADIUS } from '@/lib/design/onboarding'
import { formatPhoneInput, isValidPhoneInput } from '@/lib/format-phone'
import type { OperatorConfigurationState } from '@/lib/operator-config/types'
import { uploadAvatar } from '@/lib/services/upload'
import { trpc } from '@/lib/trpc'

function slugify(value: string) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function getInitials(value: string) {
  return value
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('')
}

const TIMEZONE_OPTIONS = [
  'America/Los_Angeles',
  'America/Denver',
  'America/Chicago',
  'America/New_York',
  'America/Phoenix',
  'America/Anchorage',
  'Pacific/Honolulu',
  'America/Toronto',
  'America/Vancouver',
  'America/Mexico_City',
  'America/Sao_Paulo',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Madrid',
  'Europe/Rome',
  'Europe/Amsterdam',
  'Europe/Zurich',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Australia/Sydney',
  'Australia/Perth',
]

const timezoneLabels: Record<string, string> = {
  'America/Los_Angeles': 'Pacific Time',
  'America/Denver': 'Mountain Time',
  'America/Chicago': 'Central Time',
  'America/New_York': 'Eastern Time',
  'America/Phoenix': 'Arizona Time',
  'America/Anchorage': 'Alaska Time',
  'Pacific/Honolulu': 'Hawaii Time',
  'America/Toronto': 'Toronto',
  'America/Vancouver': 'Vancouver',
  'America/Mexico_City': 'Mexico City',
  'America/Sao_Paulo': 'Sao Paulo',
  'Europe/London': 'London',
  'Europe/Paris': 'Paris',
  'Europe/Berlin': 'Berlin',
  'Europe/Madrid': 'Madrid',
  'Europe/Rome': 'Rome',
  'Europe/Amsterdam': 'Amsterdam',
  'Europe/Zurich': 'Zurich',
  'Asia/Dubai': 'Dubai',
  'Asia/Kolkata': 'India',
  'Asia/Singapore': 'Singapore',
  'Asia/Tokyo': 'Tokyo',
  'Australia/Sydney': 'Sydney',
  'Australia/Perth': 'Perth',
}

const inputAdornmentClass =
  'pointer-events-none absolute inset-y-0 flex items-center text-sm font-medium text-muted-foreground'
function formatTimezoneLabel(value: string) {
  const label = timezoneLabels[value]
  if (label) {
    return `${label} (${value})`
  }

  return value.replaceAll('_', ' ')
}

function DecoratedInput({
  prefix,
  suffix,
  className,
  ...props
}: {
  prefix?: ReactNode
  suffix?: ReactNode
} & ComponentPropsWithoutRef<'input'>) {
  return (
    <div className="relative">
      {prefix ? <div className={cn(inputAdornmentClass, 'left-3')}>{prefix}</div> : null}
      <Input
        className={cn(FORM.input, prefix ? 'pl-20' : '', suffix ? 'pr-16' : '', className)}
        {...props}
      />
      {suffix ? <div className={cn(inputAdornmentClass, 'right-3')}>{suffix}</div> : null}
    </div>
  )
}

interface BusinessProfileEditorProps {
  configuration: OperatorConfigurationState
  onSaved?: () => Promise<void> | void
  className?: string
  submitLabel?: string
  pendingSubmitLabel?: string
  hideSubmitButton?: boolean
  hideHeader?: boolean
  onRegisterSubmit?: (submit: () => Promise<boolean>) => void
  onDraftChange?: (draft: BusinessProfileDraft) => void
}

export interface BusinessProfileDraft {
  businessName: string
  slug: string
  bio: string | null
  phone: string | null
  timezone: string
  logoUrl: string | null
}

export function BusinessProfileEditor({
  configuration,
  onSaved,
  className,
  submitLabel = 'Save profile',
  pendingSubmitLabel = 'Saving profile…',
  hideSubmitButton = false,
  hideHeader = false,
  onRegisterSubmit,
  onDraftChange,
}: BusinessProfileEditorProps) {
  const operator = configuration.operator
  const updateMutation = trpc.operator.update.useMutation()
  const setupMutation = trpc.operator.setup.useMutation()
  const mutation = operator ? updateMutation : setupMutation
  const trpcClient = useMemo(() => createTrpcClient(), [])
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const [businessName, setBusinessName] = useState(operator?.businessName ?? '')
  const [slug, setSlug] = useState(operator?.slug ?? '')
  const [initialSlug, setInitialSlug] = useState(operator?.slug ?? '')
  const [bio, setBio] = useState(operator?.bio ?? '')
  const [phone, setPhone] = useState(formatPhoneInput(operator?.phone ?? ''))
  const [logoUrl, setLogoUrl] = useState(operator?.logoUrl ?? null)
  const [pendingLogoFile, setPendingLogoFile] = useState<File | null>(null)
  const [isUploadingLogo, setIsUploadingLogo] = useState(false)
  const [timezone, setTimezone] = useState(
    operator?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles'
  )
  const [confirmSlugChangeOpen, setConfirmSlugChangeOpen] = useState(false)

  useEffect(() => {
    setBusinessName(operator?.businessName ?? '')
    setSlug(operator?.slug ?? '')
    setInitialSlug(operator?.slug ?? '')
    setBio(operator?.bio ?? '')
    setPhone(formatPhoneInput(operator?.phone ?? ''))
    setLogoUrl(operator?.logoUrl ?? null)
    setPendingLogoFile(null)
    setIsUploadingLogo(false)
    setTimezone(
      operator?.timezone ||
        Intl.DateTimeFormat().resolvedOptions().timeZone ||
        'America/Los_Angeles'
    )
  }, [operator])

  const normalizedSlug = useMemo(() => slugify(slug), [slug])
  const isPhoneValid = !phone || isValidPhoneInput(phone)
  const hasSlugChanged = Boolean(operator && normalizedSlug !== initialSlug)
  const bookingLinkPreview = normalizedSlug || 'your-business'
  const logoFallback = getInitials(businessName.trim() || 'Your business') || 'Y'
  const timezoneOptions = useMemo(() => {
    if (timezone && !TIMEZONE_OPTIONS.includes(timezone)) {
      return [timezone, ...TIMEZONE_OPTIONS]
    }
    return TIMEZONE_OPTIONS
  }, [timezone])

  const persist = useCallback(async () => {
    const payload = {
      businessName: businessName.trim(),
      slug: normalizedSlug,
      bio: bio || undefined,
      phone: phone || undefined,
      timezone,
      logoUrl,
    }

    try {
      await mutation.mutateAsync(payload)
      setInitialSlug(normalizedSlug)
      setPendingLogoFile(null)
      toast.success('Profile saved')
      void Promise.resolve(onSaved?.()).catch(() => {})
      return true
    } catch {
      // Mutation error is surfaced below with mutation.error
      return false
    }
  }, [bio, businessName, logoUrl, mutation, normalizedSlug, onSaved, phone, timezone])

  const handleSave = useCallback(async () => {
    if (
      !businessName.trim() ||
      !normalizedSlug ||
      mutation.isPending ||
      isUploadingLogo ||
      !isPhoneValid
    ) {
      return false
    }

    if (hasSlugChanged) {
      setConfirmSlugChangeOpen(true)
      return false
    }

    if (pendingLogoFile) {
      try {
        setIsUploadingLogo(true)
        const uploadedLogoUrl = await uploadAvatar(pendingLogoFile, trpcClient)
        setLogoUrl(uploadedLogoUrl)

        const payload = {
          businessName: businessName.trim(),
          slug: normalizedSlug,
          bio: bio || undefined,
          phone: phone || undefined,
          timezone,
          logoUrl: uploadedLogoUrl,
        }

        await mutation.mutateAsync(payload)
        setInitialSlug(normalizedSlug)
        setPendingLogoFile(null)
        toast.success('Profile saved')
        void Promise.resolve(onSaved?.()).catch(() => {})
        return true
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Logo upload failed')
        return false
      } finally {
        setIsUploadingLogo(false)
      }
    }

    return persist()
  }, [
    bio,
    businessName,
    hasSlugChanged,
    isPhoneValid,
    isUploadingLogo,
    mutation,
    normalizedSlug,
    onSaved,
    pendingLogoFile,
    persist,
    phone,
    timezone,
    trpcClient,
  ])

  useEffect(() => {
    onRegisterSubmit?.(handleSave)
  }, [handleSave, onRegisterSubmit])

  useEffect(() => {
    onDraftChange?.({
      businessName,
      slug: normalizedSlug,
      bio: bio || null,
      phone: phone || null,
      timezone,
      logoUrl,
    })
  }, [bio, businessName, logoUrl, normalizedSlug, onDraftChange, phone, timezone])

  const content = (
    <>
      {hasSlugChanged ? (
        <Alert className={cn(surfaceRadii.lg, 'border-chart-4/40 bg-chart-4/10 text-foreground')}>
          <ExclamationTriangleIcon />
          <AlertTitle>Booking link will change</AlertTitle>
          <AlertDescription>
            Saving this slug update can break existing booking links that use
            <span className="mx-1 font-mono text-xs">/book/{initialSlug}</span>.
          </AlertDescription>
        </Alert>
      ) : null}

      <div className={LAYOUT.grid.identity}>
        <div className="self-start lg:self-center">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0] ?? null
              setPendingLogoFile(file)
              if (file) {
                setLogoUrl(URL.createObjectURL(file))
              }
            }}
          />

          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className={cn(RADIUS.avatar, 'transition-transform duration-200 active:scale-[0.98]')}
            aria-label="Upload logo"
          >
            <Avatar className="h-20 w-20 border border-border/60 bg-background shadow-none transition-colors duration-200 hover:border-foreground/20">
              {logoUrl ? <AvatarImage src={logoUrl} alt={businessName || 'Business logo'} /> : null}
              <AvatarFallback className="bg-muted text-sm font-semibold text-foreground">
                {logoFallback}
              </AvatarFallback>
            </Avatar>
          </button>
        </div>

        <div className={FORM.fieldStack}>
          <Label htmlFor="business-name">Business name</Label>
          <Input
            id="business-name"
            value={businessName}
            autoComplete="organization"
            className={FORM.input}
            onChange={(event) => {
              setBusinessName(event.target.value)
              if (!configuration.operator) {
                setSlug(slugify(event.target.value))
              }
            }}
            placeholder="Northline Mobile Detailing"
          />
        </div>

        <div className={FORM.fieldStack}>
          <Label htmlFor="business-slug">Booking link</Label>
          <DecoratedInput
            id="business-slug"
            value={slug}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            prefix="/book/"
            onChange={(event) => setSlug(slugify(event.target.value))}
            placeholder="northline-detailing"
          />
          <p className={FORM.fieldHint}>zippity.app/book/{bookingLinkPreview}</p>
        </div>
      </div>

      <div className={FORM.fieldStack}>
        <Label htmlFor="business-bio">Short intro</Label>
        <Textarea
          id="business-bio"
          rows={5}
          value={bio}
          className={FORM.textarea}
          onChange={(event) => setBio(event.target.value)}
          placeholder="Mobile barber serving Oakland and Berkeley. Clean fades, quick house calls, easy rebooking."
        />
      </div>

      <div className={LAYOUT.grid.twoColumn}>
        <div className={FORM.fieldStack}>
          <Label htmlFor="business-phone">Phone</Label>
          <Input
            id="business-phone"
            value={phone}
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            className={FORM.input}
            onChange={(event) => setPhone(formatPhoneInput(event.target.value))}
            placeholder="(415) 555-0148"
          />
          {!isPhoneValid ? (
            <p className="text-xs text-destructive">
              Enter a valid phone number, for example (415) 555-0148.
            </p>
          ) : null}
        </div>

        <div className={FORM.fieldStack}>
          <Label htmlFor="business-timezone">Time zone</Label>
          <Select value={timezone} onValueChange={setTimezone}>
            <SelectTrigger id="business-timezone" className={FORM.selectTrigger}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className={FORM.selectContent}>
              {timezoneOptions.map((option) => (
                <SelectItem key={option} value={option}>
                  {formatTimezoneLabel(option)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      {!hideSubmitButton ? (
        <Button
          onClick={() => void handleSave()}
          disabled={
            mutation.isPending ||
            isUploadingLogo ||
            !businessName.trim() ||
            !normalizedSlug ||
            !isPhoneValid
          }
        >
          {mutation.isPending ? pendingSubmitLabel : submitLabel}
        </Button>
      ) : null}
      {mutation.error ? <p className="text-sm text-destructive">{mutation.error.message}</p> : null}
    </>
  )

  return (
    <>
      {hideHeader ? (
        <PremiumPanel className={cn(FORM.stepPanel, className)}>
          <CardContent className={FORM.stepContent}>{content}</CardContent>
        </PremiumPanel>
      ) : (
        <AppWorkspaceContentPanel
          eyebrow="Profile"
          description="Business identity and public booking details."
          className={className}
        >
          <WorkspaceSectionCard className={cn(FORM.stepContent, 'py-5 md:py-6')}>
            {content}
          </WorkspaceSectionCard>
        </AppWorkspaceContentPanel>
      )}

      <Dialog open={confirmSlugChangeOpen} onOpenChange={setConfirmSlugChangeOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Change booking slug?</DialogTitle>
            <DialogDescription>
              Existing booking links that use <span className="font-mono">/book/{initialSlug}</span>{' '}
              may stop working after this update.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmSlugChangeOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                setConfirmSlugChangeOpen(false)
                void persist()
              }}
            >
              Save anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
